import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  useMediaQuery,
  Button,
  Stack,
  CircularProgress,
  Alert,
  Paper,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import React, { useState, useEffect, useRef } from 'react';
import axiosInstance from '../../utils/axiosInstance';
import { getUser } from '../../services/authService';
import ReplayIcon from '@mui/icons-material/Replay';
import SaveIcon from '@mui/icons-material/Save';
import InfoIcon from '@mui/icons-material/Info';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ListAltIcon from '@mui/icons-material/ListAlt';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';

// 다크 모드 스타일 상수
const darkModeStyles = {
  container: {
    p: { xs: 2, md: 4 },
    background: 'linear-gradient(180deg, rgba(25,25,38,0.95) 0%, rgba(25,32,48,0.95) 100%)',
    minHeight: '100vh'
  },
  contentBox: {
    display: 'grid',
    gap: 2,
    background: 'linear-gradient(145deg, rgba(35,35,50,0.9) 0%, rgba(35,45,65,0.85) 100%)',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
    p: 3,
    borderRadius: '16px',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    backdropFilter: 'blur(8px)',
  },
  sectionBox: {
    background: 'rgba(30,30,45,0.5)',
    borderRadius: '12px',
    border: '1px solid rgba(255, 255, 255, 0.05)',
    p: 2.5,
    mb: 3,
    position: 'relative'
  },
  title: {
    color: '#fff',
    fontWeight: 'bold',
    letterSpacing: '0.5px',
    textShadow: '0 2px 4px rgba(0,0,0,0.3)',
    mb: 5
  },
  sectionTitle: {
    color: '#60a5fa',
    fontWeight: 'bold',
    mb: 1
  },
  caption: {
    display: 'block',
    color: 'rgba(255, 255, 255, 0.5)',
    mt: 0.5
  },
  textField: {
    '& .MuiInputBase-root': {
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      borderRadius: '8px',
      color: '#fff'
    },
    '& .MuiInputLabel-root': {
      color: 'rgba(255, 255, 255, 0.7)'
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255, 255, 255, 0.15)'
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255, 255, 255, 0.3)'
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#60a5fa'
    }
  },
  select: {
    '& .MuiInputBase-root': {
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      borderRadius: '8px',
      color: '#fff'
    },
    '& .MuiInputLabel-root': {
      color: 'rgba(255, 255, 255, 0.7)'
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255, 255, 255, 0.15)'
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255, 255, 255, 0.3)'
    },
    '& .MuiSelect-icon': {
      color: 'rgba(255, 255, 255, 0.7)'
    }
  },
  menuItem: {
    '&.MuiMenuItem-root': {
      color: '#fff',
      backgroundColor: 'rgba(35, 35, 50, 0.95)',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
      },
      '&.Mui-selected': {
        backgroundColor: 'rgba(96, 165, 250, 0.2)',
        '&:hover': {
          backgroundColor: 'rgba(96, 165, 250, 0.3)',
        }
      }
    }
  },
  resetButton: {
    px: 2,
    py: 1,
    borderRadius: '8px',
    color: '#fff',
    borderColor: 'rgba(255, 255, 255, 0.3)',
    '&:hover': {
      borderColor: 'rgba(255, 255, 255, 0.5)',
      backgroundColor: 'rgba(255, 255, 255, 0.05)'
    },
    minWidth: '40px',
    height: '36px'
  },
  sectionResetButton: {
    position: 'absolute',
    top: '12px',
    right: '12px',
    minWidth: 'unset',
    width: '36px',
    height: '36px',
    p: 0,
    borderRadius: '50%',
    color: '#fff',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)'
    }
  },
  deleteButton: {
    position: 'absolute',
    top: '12px',
    right: '12px',
    minWidth: 'unset',
    width: '36px',
    height: '36px',
    p: 0,
    borderRadius: '50%',
    color: '#fff',
    backgroundColor: 'rgba(220, 53, 69, 0.2)',
    '&:hover': {
      backgroundColor: 'rgba(220, 53, 69, 0.3)'
    }
  },
  saveButton: {
    px: 4,
    py: 1.5,
    borderRadius: '10px',
    backgroundColor: '#4caf50',
    boxShadow: '0 4px 10px rgba(76, 175, 80, 0.3)',
    '&:hover': {
      backgroundColor: '#388e3c',
      boxShadow: '0 6px 12px rgba(76, 175, 80, 0.4)',
      transform: 'translateY(-2px)'
    },
    transition: 'all 0.3s ease'
  },
  alertError: {
    backgroundColor: 'rgba(220, 38, 38, 0.1)',
    color: '#f87171',
    border: '1px solid rgba(220, 38, 38, 0.2)',
    '& .MuiAlert-icon': {
      color: '#f87171'
    }
  },
  alertSuccess: {
    backgroundColor: 'rgba(34, 197, 94, 0.1)',
    color: '#4ade80',
    border: '1px solid rgba(34, 197, 94, 0.2)',
    '& .MuiAlert-icon': {
      color: '#4ade80'
    }
  },
  infoCard: {
    backgroundColor: 'rgba(96, 165, 250, 0.05)',
    borderRadius: '8px',
    border: '1px solid rgba(96, 165, 250, 0.1)',
    p: 2,
    mt: 1,
    mb: 2
  },
  infoTitle: {
    fontSize: '0.875rem',
    fontWeight: 'bold',
    color: 'rgba(255, 255, 255, 0.9)',
    mb: 1,
    display: 'flex',
    alignItems: 'center',
    gap: 1
  },
  infoText: {
    fontSize: '0.875rem',
    color: 'rgba(255, 255, 255, 0.7)'
  },
  infoValue: {
    fontSize: '1.125rem',
    fontWeight: 'bold',
    color: '#60a5fa',
    mt: 0.5
  },
  infoValueChanged: {
    fontSize: '1.125rem',
    fontWeight: 'bold',
    color: '#ffc107',
    mt: 0.5
  },
  divider: {
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
    my: 2
  },
  chip: {
    backgroundColor: 'rgba(96, 165, 250, 0.2)',
    color: '#60a5fa',
    fontWeight: 'bold',
    borderRadius: '16px',
    border: '1px solid rgba(96, 165, 250, 0.3)',
    height: '24px',
    fontSize: '0.75rem',
    ml: 1
  },
  chipChanged: {
    backgroundColor: 'rgba(255, 193, 7, 0.2)',
    color: '#ffc107',
    fontWeight: 'bold',
    borderRadius: '16px',
    border: '1px solid rgba(255, 193, 7, 0.3)',
    height: '24px',
    fontSize: '0.6rem',
    ml: 1
  },
  // 테이블 스타일
  tableContainer: {
    backgroundColor: 'rgba(35, 35, 50, 0.5)',
    borderRadius: '12px',
    border: '1px solid rgba(255, 255, 255, 0.05)',
    overflow: 'hidden',
    marginTop: 3,
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
  },
  tableHeader: {
    backgroundColor: 'rgba(30, 30, 45, 0.9)',
  },
  tableHeaderCell: {
    color: '#60a5fa',
    fontWeight: 'bold',
    borderBottomColor: 'rgba(255, 255, 255, 0.1)',
    padding: 2
  },
  tableCell: {
    color: '#fff',
    borderBottomColor: 'rgba(255, 255, 255, 0.05)',
    padding: 2
  },
  tableBetAmount: {
    color: '#60a5fa',
    fontWeight: 'bold',
    fontSize: '1rem'
  },
  tableTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    color: '#fff',
    marginBottom: 2,
    fontWeight: 'bold'
  },
  refreshButton: {
    color: '#fff',
    borderColor: '#60a5fa',
    '&:hover': {
      backgroundColor: 'rgba(96, 165, 250, 0.1)',
      borderColor: '#60a5fa',
    }
  }
};

const BetLimit = () => {
  const isNonMobile = useMediaQuery('(min-width:600px)');
  const isMounted = useRef(true);
  
  // 상태 관리
  const [evolutionGames, setEvolutionGames] = useState([]);
  const [gameCategories, setGameCategories] = useState(['casino', 'slot']);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // 현재 활성화된 드롭다운 타입
  const [activeDropdown, setActiveDropdown] = useState(null);
  
  // 선택된 값 상태
  const [selectedGame, setSelectedGame] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedVendor, setSelectedVendor] = useState('');
  
  // 베팅 금액 상태
  const [betAmount, setBetAmount] = useState(0);
  const [globalAmount, setGlobalAmount] = useState(0);

  // 현재 설정된 값들 상태
  const [currentSettings, setCurrentSettings] = useState({
    global: 0,
    games: {},
    categories: {
      casino: 0,
      slot: 0
    },
    vendors: {}
  });

  // 원본 설정값 (서버에서 가져온 실제 값)
  const [originalSettings, setOriginalSettings] = useState({
    global: 0,
    games: {},
    categories: {
      casino: 0,
      slot: 0
    },
    vendors: {}
  });

  // 로딩 상태
  const [fetchingSettings, setFetchingSettings] = useState(true);
  
  // 베팅 제한 설정 목록을 보관할 state 추가
  const [betLimitSettings, setBetLimitSettings] = useState([]);
  const [loadingSettings, setLoadingSettings] = useState(false);

  // 인풋 포커스 핸들러
  const handleInputFocus = (e, setter, currentValue) => {
    if (currentValue === 0 || currentValue === '0') {
      setter('');
    }
  };

  // 인풋 블러 핸들러
  const handleInputBlur = (e, setter, currentValue) => {
    if (currentValue === '' || currentValue === null) {
      setter(0);
    }
  };

  // 메시지 초기화 함수
  const clearMessages = () => {
    setError(null);
    setSuccess(null);
  };

  // 컴포넌트 마운트 시 데이터 로드
  useEffect(() => {
    fetchEvolutionGames();
    fetchVendors();
    fetchCurrentSettings();
    fetchBetLimitSettings();

    return () => {
      isMounted.current = false;
    };
  }, []);

  // 현재 설정된 값 가져오기
  const fetchCurrentSettings = async () => {
    setFetchingSettings(true);
    try {
      const user = getUser();

      // 유저 정보에서 글로벌 max_bet 값 가져오기
      if (user && user.max_bet) {
        setCurrentSettings(prev => ({
          ...prev,
          global: user.max_bet
        }));
        setOriginalSettings(prev => ({
          ...prev,
          global: user.max_bet
        }));
      }
      
      // 게임사별 설정 가져오기
      const vendorResponse = await axiosInstance.get(`/games/agent/company?agent_id=${user?.id || 'admin'}`);
      
      if (vendorResponse.data && vendorResponse.data.success) {
        const vendorData = vendorResponse.data.vendors;
        const vendorSettings = {};
        
        Object.keys(vendorData).forEach(vendorName => {
          if (vendorData[vendorName] && vendorData[vendorName].length > 0) {
            vendorSettings[vendorName] = vendorData[vendorName][0].max_bet || 0;
          }
        });
        
        setCurrentSettings(prev => ({
          ...prev,
          vendors: vendorSettings
        }));
        setOriginalSettings(prev => ({
          ...prev,
          vendors: vendorSettings
        }));
      }
      
    } catch (error) {
      console.error('현재 설정값 가져오기 실패:', error);
    } finally {
      setFetchingSettings(false);
    }
  };

  // 에볼루션 게임 목록 가져오기
  const fetchEvolutionGames = async () => {
    try {
      const user = getUser();
      const response = await axiosInstance.get(`/games/agent/provider?agent_id=${user?.id || 'admin'}&provider=evolution`);
      
      if (!isMounted.current) return;
      
      if (response.data && response.data.games) {
        const games = response.data.games.map(game => ({
          id: game.title_en,
          name: game.title_en,
          category: game.category,
          type: game.type,
          use: game.use,
          max_bet: typeof game.max_bet === 'number' ? game.max_bet : 0
        }));
        
        setEvolutionGames(games);
        
        // 게임별 현재 설정값 업데이트
        const gameSettings = {};
        games.forEach(game => {
          gameSettings[game.id] = typeof game.max_bet === 'number' ? game.max_bet : 0;
        });
        
        setCurrentSettings(prev => ({
          ...prev,
          games: gameSettings
        }));
        setOriginalSettings(prev => ({
          ...prev,
          games: gameSettings
        }));
      }
    } catch (error) {
      if (isMounted.current) {
        console.error('에볼루션 게임 목록 가져오기 실패:', error);
      }
    }
  };

  // 벤더 목록 가져오기
  const fetchVendors = async () => {
    try {
      const response = await axiosInstance.get('/games/vendor');
      
      if (!isMounted.current) return;
      
      if (response.data && response.data.success) {
        const vendorsList = Object.keys(response.data.vendors || {}).map(name => ({
          id: name,
          name: name
        }));
        
        setVendors(vendorsList);
      }
    } catch (error) {
      if (isMounted.current) {
        console.error('벤더 목록 가져오기 실패:', error);
      }
    }
  };
  
  // BetLimit 설정 데이터 가져오기 함수
  const fetchBetLimitSettings = async () => {
    setLoadingSettings(true);
    try {
      const user = getUser();
      const response = await axiosInstance.get(`/agent/gaming/set/data?agent_id=${user?.id || 'admin'}`);
      
      if (!isMounted.current) return;
      
      if (response.data && response.data.success) {
        const settings = response.data.data || [];
        
        const formattedSettings = settings.map(setting => ({
          id: setting.id || Math.random().toString(36).substr(2, 9),
          provider: setting.provider || '-',
          gameName: setting.game_name || '전체 게임',
          maxBet: setting.max_bet || 0,
          maxWin: setting.max_win || 0,
          updatedAt: setting.updated_at || '-'
        }));
        
        setBetLimitSettings(formattedSettings);
      }
    } catch (error) {
      if (isMounted.current) {
        console.error('베팅 제한 설정 가져오기 실패:', error);
      }
    } finally {
      setLoadingSettings(false);
    }
  };

  // 금액 포맷팅 함수
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ko-KR').format(amount);
  };

  // 변경 여부 확인 함수
  const isValueChanged = (type, key = null) => {
    if (type === 'global') {
      return Number(globalAmount) !== originalSettings.global;
    } else if (type === 'game' && key) {
      return Number(betAmount) !== (originalSettings.games[key] || 0);
    } else if (type === 'category' && key) {
      return Number(betAmount) !== (originalSettings.categories[key] || 0);
    } else if (type === 'vendor' && key) {
      return Number(betAmount) !== (originalSettings.vendors[key] || 0);
    }
    return false;
  };

  // 전역 금액 변경 처리 (즉시 반영)
  const handleGlobalAmountChange = (e) => {
    const value = e.target.value;
    setGlobalAmount(value);
    
    // 즉시 currentSettings 업데이트
    setCurrentSettings(prev => ({
      ...prev,
      global: Number(value) || 0
    }));
    
    if (value > 0) {
      setActiveDropdown('global');
      setSelectedGame('');
      setSelectedCategory('');
      setSelectedVendor('');
      setBetAmount(0);
    } else if (value === '0' || value === 0 || value === '') {
      if (value === '' || value === 0) {
        setActiveDropdown(null);
      }
    }
  };

  // 베팅 금액 변경 처리 (즉시 반영)
  const handleBetAmountChange = (e) => {
    const value = e.target.value;
    setBetAmount(value);
    
    // 즉시 currentSettings 업데이트
    if (activeDropdown === 'game' && selectedGame) {
      setCurrentSettings(prev => ({
        ...prev,
        games: {
          ...prev.games,
          [selectedGame]: Number(value) || 0
        }
      }));
    } else if (activeDropdown === 'category' && selectedCategory) {
      setCurrentSettings(prev => ({
        ...prev,
        categories: {
          ...prev.categories,
          [selectedCategory]: Number(value) || 0
        }
      }));
    } else if (activeDropdown === 'vendor' && selectedVendor) {
      setCurrentSettings(prev => ({
        ...prev,
        vendors: {
          ...prev.vendors,
          [selectedVendor]: Number(value) || 0
        }
      }));
    }
  };

  // 게임 선택 처리
  const handleGameChange = (event) => {
    setGlobalAmount(0);
    setSelectedCategory('');
    setSelectedVendor('');
    
    setSelectedGame(event.target.value);
    setActiveDropdown('game');
    
    const currentGameBet = originalSettings.games[event.target.value] || 0;
    setBetAmount(currentGameBet);
    
    // 즉시 currentSettings 업데이트
    setCurrentSettings(prev => ({
      ...prev,
      games: {
        ...prev.games,
        [event.target.value]: currentGameBet
      }
    }));
  };

  // 카테고리 선택 처리
  const handleCategoryChange = (event) => {
    setGlobalAmount(0);
    setSelectedGame('');
    setSelectedVendor('');
    
    setSelectedCategory(event.target.value);
    setActiveDropdown('category');
    
    const currentCategoryBet = originalSettings.categories[event.target.value] || 0;
    setBetAmount(currentCategoryBet);
    
    // 즉시 currentSettings 업데이트
    setCurrentSettings(prev => ({
      ...prev,
      categories: {
        ...prev.categories,
        [event.target.value]: currentCategoryBet
      }
    }));
  };

  // 벤더 선택 처리
  const handleVendorChange = (event) => {
    setGlobalAmount(0);
    setSelectedGame('');
    setSelectedCategory('');
    
    setSelectedVendor(event.target.value);
    setActiveDropdown('vendor');
    
    const currentVendorBet = originalSettings.vendors[event.target.value] || 0;
    setBetAmount(currentVendorBet);
    
    // 즉시 currentSettings 업데이트
    setCurrentSettings(prev => ({
      ...prev,
      vendors: {
        ...prev.vendors,
        [event.target.value]: currentVendorBet
      }
    }));
  };

  // 섹션별 리셋 핸들러
  const handleSectionReset = (section) => {
    clearMessages();
    
    if (section === 'global') {
      setGlobalAmount(0);
      setCurrentSettings(prev => ({
        ...prev,
        global: originalSettings.global
      }));
      if (activeDropdown === 'global') {
        setActiveDropdown(null);
      }
    } else if (section === 'game') {
      setSelectedGame('');
      if (activeDropdown === 'game') {
        setActiveDropdown(null);
        setBetAmount(0);
      }
    } else if (section === 'category') {
      setSelectedCategory('');
      if (activeDropdown === 'category') {
        setActiveDropdown(null);
        setBetAmount(0);
      }
    } else if (section === 'vendor') {
      setSelectedVendor('');
      if (activeDropdown === 'vendor') {
        setActiveDropdown(null);
        setBetAmount(0);
      }
    }
  };

  // 폼 제출 핸들러
  const handleSubmit = async () => {
    if (!activeDropdown) return;
    
    setLoading(true);
    clearMessages();
    
    try {
      const user = getUser();
      if (!user || !user.id) {
        setError('로그인 정보를 찾을 수 없습니다.');
        setLoading(false);
        return;
      }
      
      let targetName = '';
      let type = '';
      let maxBetAmount = 0;
      
      if (activeDropdown === 'global') {
        type = 'global';
        maxBetAmount = globalAmount;
      } else if (activeDropdown === 'game') {
        type = 'game';
        targetName = selectedGame;
        maxBetAmount = betAmount;
      } else if (activeDropdown === 'category') {
        type = 'category';
        targetName = selectedCategory;
        maxBetAmount = betAmount;
      } else if (activeDropdown === 'vendor') {
        type = 'vendor';
        targetName = selectedVendor;
        maxBetAmount = betAmount;
      }
      
      // API 호출
      const response = await axiosInstance.post('/games/agent/maxbet/update', {
        agent_id: user.id,
        type: type,
        target_name: targetName,
        max_bet_amount: maxBetAmount
      });
      
      if (!isMounted.current) return;
      
      if (response.data && response.data.success) {
        setSuccess(response.data.message || '최대 베팅 금액이 성공적으로 저장되었습니다.');
        
        // 원본 설정값도 업데이트 (저장 완료)
        if (activeDropdown === 'global') {
          setOriginalSettings(prev => ({
            ...prev,
            global: Number(maxBetAmount)
          }));
        } else if (activeDropdown === 'game') {
          setOriginalSettings(prev => ({
            ...prev,
            games: {
              ...prev.games,
              [targetName]: Number(maxBetAmount)
            }
          }));
        } else if (activeDropdown === 'category') {
          setOriginalSettings(prev => ({
            ...prev,
            categories: {
              ...prev.categories,
              [targetName]: Number(maxBetAmount)
            }
          }));
        } else if (activeDropdown === 'vendor') {
          setOriginalSettings(prev => ({
            ...prev,
            vendors: {
              ...prev.vendors,
              [targetName]: Number(maxBetAmount)
            }
          }));
        }
        
        fetchBetLimitSettings();
      } else {
        throw new Error(response.data.message || '저장에 실패했습니다.');
      }
    } catch (error) {
      if (!isMounted.current) return;
      
      console.error('베팅 금액 설정 실패:', error);
      setError(error.response?.data?.message || error.message || '저장 중 오류가 발생했습니다.');
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  // 전체 리셋 핸들러
  const handleReset = () => {
    setActiveDropdown(null);
    setSelectedGame('');
    setSelectedCategory('');
    setSelectedVendor('');
    setBetAmount(0);
    setGlobalAmount(0);
    
    // 원본 설정값으로 복원
    setCurrentSettings({...originalSettings});
    clearMessages();
  };
  
  // 리셋 핸들러 함수 추가 (즉시 반영)
  const handleResetSettings = async (category) => {
    setLoading(true);
    clearMessages();
    
    // 즉시 UI 업데이트 (낙관적 업데이트)
    if (category === 'agent') {
      setCurrentSettings(prev => ({
        ...prev,
        global: 0
      }));
      setOriginalSettings(prev => ({
        ...prev,
        global: 0
      }));
      setGlobalAmount(0);
      if (activeDropdown === 'global') {
        setActiveDropdown(null);
      }
    } else if (category === 'provider') {
      const resetVendors = {};
      Object.keys(currentSettings.vendors).forEach(vendor => {
        resetVendors[vendor] = 0;
      });
      setCurrentSettings(prev => ({
        ...prev,
        vendors: resetVendors
      }));
      setOriginalSettings(prev => ({
        ...prev,
        vendors: resetVendors
      }));
      if (activeDropdown === 'vendor') {
        setSelectedVendor('');
        setActiveDropdown(null);
        setBetAmount(0);
      }
    } else if (category === 'game') {
      const resetGames = {};
      Object.keys(currentSettings.games).forEach(game => {
        resetGames[game] = 0;
      });
      setCurrentSettings(prev => ({
        ...prev,
        games: resetGames
      }));
      setOriginalSettings(prev => ({
        ...prev,
        games: resetGames
      }));
      if (activeDropdown === 'game') {
        setSelectedGame('');
        setActiveDropdown(null);
        setBetAmount(0);
      }
    }
    
    try {
      const user = getUser();
      if (!user || !user.id) {
        setError('로그인 정보를 찾을 수 없습니다.');
        setLoading(false);
        return;
      }
      
      const response = await axiosInstance.post('/agent/gaming/reset/data', {
        agent_id: user.id,
        category: category,
        type: 'max_bet'
      });
      
      if (!isMounted.current) return;
      
      if (response.data && response.data.success) {
        const categoryLabel = 
          category === 'agent' ? '에이전트' : 
          category === 'provider' ? '게임 제공업체' : 
          category === 'game' ? '개별 게임' : '';
          
        setSuccess(`${categoryLabel}의 최대 베팅 금액 설정이 성공적으로 초기화되었습니다.`);
        
        // API 성공 시에는 현재 설정값 다시 가져오기 (서버와 동기화)
        fetchCurrentSettings();
        fetchBetLimitSettings();
      } else {
        // API 실패 시 원래 값으로 롤백
        fetchCurrentSettings();
        throw new Error(response.data.message || '초기화에 실패했습니다.');
      }
    } catch (error) {
      if (!isMounted.current) return;
      
      // API 실패 시 원래 값으로 롤백
      fetchCurrentSettings();
      
      console.error('베팅 금액 초기화 실패:', error);
      setError(error.response?.data?.message || error.message || '초기화 중 오류가 발생했습니다.');
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  return (
    <Box sx={darkModeStyles.container}>
      <Box
        sx={{
          ...darkModeStyles.contentBox,
          gridTemplateColumns: isNonMobile ? 'repeat(3, 1fr)' : 'repeat(1, 1fr)',
        }}
      >
        {/* 최대 베팅 금액 제한 타이틀 */}
        <Box sx={{ gridColumn: 'span 3' }}>
          <Typography variant="h4" sx={darkModeStyles.title}>
            최대 베팅 금액 제한
          </Typography>
        </Box>

        {/* 알림 메시지 표시 */}
        {error && (
          <Box sx={{ gridColumn: 'span 3', mb: 2 }}>
            <Alert severity="error" sx={darkModeStyles.alertError}>{error}</Alert>
          </Box>
        )}
        
        {success && (
          <Box sx={{ gridColumn: 'span 3', mb: 2 }}>
            <Alert severity="success" sx={darkModeStyles.alertSuccess}>{success}</Alert>
          </Box>
        )}

        {/* 에이전트 전체 설정 (전역 설정) */}
        <Box sx={{ gridColumn: 'span 3' }}>
          <Box sx={darkModeStyles.sectionBox}>
            <Button 
              variant="contained" 
              size="small" 
              onClick={() => handleResetSettings('agent')}
              disabled={loading}
              sx={darkModeStyles.deleteButton}
              title="에이전트 전체 설정 초기화"
            >
              <DeleteForeverIcon fontSize="small" />
            </Button>

            <Typography variant="h6" sx={darkModeStyles.sectionTitle}>
              에이전트 전체 설정
            </Typography>
            
            <TextField
              fullWidth
              type="number"
              value={globalAmount}
              onChange={handleGlobalAmountChange}
              size="small"
              placeholder="0"
              disabled={activeDropdown !== null && activeDropdown !== 'global'}
              InputProps={{
                inputProps: { min: 0 }
              }}
              onFocus={(e) => handleInputFocus(e, setGlobalAmount, globalAmount)}
              onBlur={(e) => handleInputBlur(e, setGlobalAmount, globalAmount)}
              sx={darkModeStyles.textField}
            />
            
            <Typography variant="caption" sx={darkModeStyles.caption}>
              전역 설정이 게임 설정보다 우선시 됩니다.
            </Typography>

            {/* 현재 설정값 표시 (즉시 반영) */}
            {!fetchingSettings && (
              <Box sx={darkModeStyles.infoCard}>
                <Typography sx={darkModeStyles.infoTitle}>
                  <InfoIcon fontSize="small" sx={{ color: '#60a5fa' }} />
                  {isValueChanged('global') ? '변경될 값 (미저장)' : '현재 전체 설정값'}
                  {isValueChanged('global') && (
                    <Chip 
                      label="변경됨" 
                      size="small" 
                      sx={darkModeStyles.chipChanged}
                    />
                  )}
                </Typography>
                <Typography sx={isValueChanged('global') ? darkModeStyles.infoValueChanged : darkModeStyles.infoValue}>
                  {formatCurrency(currentSettings.global)} 원
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {/* 게임별 설정 - 에볼루션 게임 목록 */}
        <Box sx={{ gridColumn: !isNonMobile && 'span 3' }}>
          <Box sx={darkModeStyles.sectionBox}>
            <Button 
              variant="contained" 
              size="small" 
              onClick={() => handleResetSettings('game')}
              disabled={loading}
              sx={darkModeStyles.deleteButton}
              title="모든 게임 설정 초기화"
            >
              <DeleteForeverIcon fontSize="small" />
            </Button>

            <Typography variant="h6" sx={darkModeStyles.sectionTitle}>
              게임별 설정
            </Typography>
            
            <FormControl fullWidth size="small" sx={darkModeStyles.select}>
              <InputLabel>게임</InputLabel>
              <Select
                value={selectedGame}
                onChange={handleGameChange}
                label="게임"
                disabled={activeDropdown !== null && activeDropdown !== 'game'}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      backgroundColor: 'rgba(35, 35, 50, 0.95)',
                      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '8px'
                    }
                  }
                }}
              >
                {evolutionGames.length > 0 ? (
                  evolutionGames.map(game => (
                    <MenuItem key={game.id} value={game.id} sx={darkModeStyles.menuItem}>
                      {game.name}
                      {currentSettings.games[game.id] > 0 && (
                        <Chip 
                          label={`${formatCurrency(currentSettings.games[game.id])}원`} 
                          size="small" 
                          sx={isValueChanged('game', game.id) ? darkModeStyles.chipChanged : darkModeStyles.chip} 
                        />
                      )}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled sx={darkModeStyles.menuItem}>게임 데이터가 없습니다</MenuItem>
                )}
              </Select>
            </FormControl>
            
            {activeDropdown === 'game' && (
              <>
                <TextField
                  fullWidth
                  type="number"
                  value={betAmount}
                  onChange={handleBetAmountChange}
                  margin="normal"
                  size="small"
                  placeholder="0"
                  InputProps={{
                    inputProps: { min: 0 }
                  }}
                  onFocus={(e) => handleInputFocus(e, setBetAmount, betAmount)}
                  onBlur={(e) => handleInputBlur(e, setBetAmount, betAmount)}
                  sx={darkModeStyles.textField}
                />
                <Typography variant="caption" sx={darkModeStyles.caption}>
                  최대 베팅 금액 (단위: 원)
                </Typography>
              </>
            )}

            {/* 선택된 게임에 대한 현재 설정값 표시 (즉시 반영) */}
            {selectedGame && currentSettings.games[selectedGame] !== undefined && (
              <Box sx={darkModeStyles.infoCard}>
                <Typography sx={darkModeStyles.infoTitle}>
                  <InfoIcon fontSize="small" sx={{ color: '#60a5fa' }} />
                  {isValueChanged('game', selectedGame) ? '변경될 값 (미저장)' : '현재 설정값'}: {selectedGame}
                  {isValueChanged('game', selectedGame) && (
                    <Chip 
                      label="변경됨" 
                      size="small" 
                      sx={darkModeStyles.chipChanged}
                    />
                  )}
                </Typography>
                <Typography sx={isValueChanged('game', selectedGame) ? darkModeStyles.infoValueChanged : darkModeStyles.infoValue}>
                  {formatCurrency(currentSettings.games[selectedGame])} 원
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {/* 게임 타입별 설정 - 카지노/슬롯 */}
        <Box sx={{ gridColumn: !isNonMobile && 'span 3' }}>
          <Box sx={darkModeStyles.sectionBox}>
            <Button 
              variant="contained" 
              size="small" 
              onClick={() => handleSectionReset('category')}
              sx={darkModeStyles.sectionResetButton}
            >
              <RestartAltIcon fontSize="small" />
            </Button>

            <Typography variant="h6" sx={darkModeStyles.sectionTitle}>
              게임 타입별 설정
            </Typography>
            
            <FormControl fullWidth size="small" sx={darkModeStyles.select}>
              <InputLabel>게임 타입</InputLabel>
              <Select
                value={selectedCategory}
                onChange={handleCategoryChange}
                label="게임 타입"
                disabled={activeDropdown !== null && activeDropdown !== 'category'}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      backgroundColor: 'rgba(35, 35, 50, 0.95)',
                      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '8px'
                    }
                  }
                }}
              >
                {gameCategories.map(category => (
                  <MenuItem key={category} value={category} sx={darkModeStyles.menuItem}>
                    {category === 'casino' ? '카지노' : '슬롯'}
                    {currentSettings.categories[category] > 0 && (
                      <Chip 
                        label={`${formatCurrency(currentSettings.categories[category])}원`} 
                        size="small" 
                        sx={isValueChanged('category', category) ? darkModeStyles.chipChanged : darkModeStyles.chip} 
                      />
                    )}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            {activeDropdown === 'category' && (
              <>
                <TextField
                  fullWidth
                  type="number"
                  value={betAmount}
                  onChange={handleBetAmountChange}
                  margin="normal"
                  size="small"
                  placeholder="0"
                  InputProps={{
                    inputProps: { min: 0 }
                  }}
                  onFocus={(e) => handleInputFocus(e, setBetAmount, betAmount)}
                  onBlur={(e) => handleInputBlur(e, setBetAmount, betAmount)}
                  sx={darkModeStyles.textField}
                />
                <Typography variant="caption" sx={darkModeStyles.caption}>
                  최대 베팅 금액 (단위: 원)
                </Typography>
              </>
            )}

            {/* 선택된 카테고리에 대한 현재 설정값 표시 (즉시 반영) */}
            {selectedCategory && currentSettings.categories[selectedCategory] !== undefined && (
              <Box sx={darkModeStyles.infoCard}>
                <Typography sx={darkModeStyles.infoTitle}>
                  <InfoIcon fontSize="small" sx={{ color: '#60a5fa' }} />
                  {isValueChanged('category', selectedCategory) ? '변경될 값 (미저장)' : '현재 설정값'}: {selectedCategory === 'casino' ? '카지노' : '슬롯'}
                  {isValueChanged('category', selectedCategory) && (
                    <Chip 
                      label="변경됨" 
                      size="small" 
                      sx={darkModeStyles.chipChanged}
                    />
                  )}
                </Typography>
                <Typography sx={isValueChanged('category', selectedCategory) ? darkModeStyles.infoValueChanged : darkModeStyles.infoValue}>
                  {formatCurrency(currentSettings.categories[selectedCategory])} 원
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {/* 게임사별 설정 - 모든 프로바이더 */}
        <Box sx={{ gridColumn: !isNonMobile && 'span 3' }}>
          <Box sx={darkModeStyles.sectionBox}>
            <Button 
              variant="contained" 
              size="small" 
              onClick={() => handleResetSettings('provider')}
              disabled={loading}
              sx={darkModeStyles.deleteButton}
              title="모든 게임사 설정 초기화"
            >
              <DeleteForeverIcon fontSize="small" />
            </Button>

            <Typography variant="h6" sx={darkModeStyles.sectionTitle}>
              게임사별 설정
            </Typography>
            
            <FormControl fullWidth size="small" sx={darkModeStyles.select}>
              <InputLabel>게임사</InputLabel>
              <Select
                value={selectedVendor}
                onChange={handleVendorChange}
                label="게임사"
                disabled={activeDropdown !== null && activeDropdown !== 'vendor'}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      backgroundColor: 'rgba(35, 35, 50, 0.95)',
                      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '8px'
                    }
                  }
                }}
              >
                {vendors.map(vendor => (
                  <MenuItem key={vendor.id} value={vendor.id} sx={darkModeStyles.menuItem}>
                    {vendor.name}
                    {currentSettings.vendors[vendor.id] > 0 && (
                      <Chip 
                        label={`${formatCurrency(currentSettings.vendors[vendor.id])}원`} 
                        size="small" 
                        sx={isValueChanged('vendor', vendor.id) ? darkModeStyles.chipChanged : darkModeStyles.chip} 
                      />
                    )}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            {activeDropdown === 'vendor' && (
              <>
                <TextField
                  fullWidth
                  type="number"
                  value={betAmount}
                  onChange={handleBetAmountChange}
                  margin="normal"
                  size="small"
                  placeholder="0"
                  InputProps={{
                    inputProps: { min: 0 }
                  }}
                  onFocus={(e) => handleInputFocus(e, setBetAmount, betAmount)}
                  onBlur={(e) => handleInputBlur(e, setBetAmount, betAmount)}
                  sx={darkModeStyles.textField}
                />
                <Typography variant="caption" sx={darkModeStyles.caption}>
                  최대 베팅 금액 (단위: 원)
                </Typography>
              </>
            )}

            {/* 선택된 벤더에 대한 현재 설정값 표시 (즉시 반영) */}
            {selectedVendor && currentSettings.vendors[selectedVendor] !== undefined && (
              <Box sx={darkModeStyles.infoCard}>
                <Typography sx={darkModeStyles.infoTitle}>
                  <InfoIcon fontSize="small" sx={{ color: '#60a5fa' }} />
                  {isValueChanged('vendor', selectedVendor) ? '변경될 값 (미저장)' : '현재 설정값'}: {selectedVendor}
                  {isValueChanged('vendor', selectedVendor) && (
                    <Chip 
                      label="변경됨" 
                      size="small" 
                      sx={darkModeStyles.chipChanged}
                    />
                  )}
                </Typography>
                <Typography sx={isValueChanged('vendor', selectedVendor) ? darkModeStyles.infoValueChanged : darkModeStyles.infoValue}>
                  {formatCurrency(currentSettings.vendors[selectedVendor])} 원
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {/* 저장 및 리셋 버튼 */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            mt: 3,
            gridColumn: 'span 3',
          }}
        >
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleReset}
              startIcon={<ReplayIcon sx={{ color: '#fff' }} />}
              sx={darkModeStyles.resetButton}
            >
              다른기능셋팅하기
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={!activeDropdown || loading}
              startIcon={loading ? <CircularProgress size={24} color="inherit" /> : <SaveIcon />}
              sx={darkModeStyles.saveButton}
            >
              {loading ? '저장 중...' : '저장'}
            </Button>
          </Stack>
        </Box>
      </Box>

      {/* 설정 목록 테이블 */}
      <Box sx={{ ...darkModeStyles.contentBox, mt: 4 }}>
        <Box sx={{ gridColumn: 'span 3' }}>
          <Typography variant="h6" sx={darkModeStyles.tableTitle}>
            <ListAltIcon sx={{ color: '#60a5fa' }} />
            최대 베팅 제한 설정 목록
          </Typography>
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button
              variant="outlined"
              startIcon={<ReplayIcon />}
              onClick={fetchBetLimitSettings}
              disabled={loadingSettings}
              sx={darkModeStyles.refreshButton}
            >
              {loadingSettings ? '로딩 중...' : '목록 새로고침'}
            </Button>
          </Box>
          
          <TableContainer component={Paper} sx={darkModeStyles.tableContainer}>
            <Table>
              <TableHead sx={darkModeStyles.tableHeader}>
                <TableRow>
                  <TableCell sx={darkModeStyles.tableHeaderCell}>게임 제공업체</TableCell>
                  <TableCell sx={darkModeStyles.tableHeaderCell}>게임명</TableCell>
                  <TableCell sx={darkModeStyles.tableHeaderCell}>최대 베팅 금액</TableCell>
                  <TableCell sx={darkModeStyles.tableHeaderCell}>최대 당첨 금액</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingSettings ? (
                  <TableRow>
                    <TableCell colSpan={4} sx={{...darkModeStyles.tableCell, textAlign: 'center', py: 3}}>
                      <CircularProgress size={32} sx={{ color: '#60a5fa' }} />
                    </TableCell>
                  </TableRow>
                ) : betLimitSettings.length > 0 ? (
                  betLimitSettings.map(setting => (
                    <TableRow key={setting.id} hover>
                      <TableCell sx={darkModeStyles.tableCell}>{setting.provider}</TableCell>
                      <TableCell sx={darkModeStyles.tableCell}>{setting.gameName}</TableCell>
                      <TableCell sx={{...darkModeStyles.tableCell, ...darkModeStyles.tableBetAmount}}>
                        {formatCurrency(setting.maxBet)} 원
                      </TableCell>
                      <TableCell sx={{...darkModeStyles.tableCell, ...darkModeStyles.tableBetAmount}}>
                        {formatCurrency(setting.maxWin)} 원
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} sx={{...darkModeStyles.tableCell, textAlign: 'center', py: 3}}>
                      설정된 베팅 제한이 없습니다.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
    </Box>
  );
};

export default BetLimit;